<template>
  <div class="email-batch-container">
    <div class="page-header">
      <h2>邮件批量发送</h2>
      <p class="page-desc">支持批量导入收件人、选择预设模板、预览内容、立即或定时发送</p>
    </div>

    <el-tabs v-model="activeTab" type="border-card">
      <el-tab-pane label="管理员通知邮箱" name="notify">
        <el-form label-width="140px" style="max-width: 640px; padding: 12px 8px;">
          <el-form-item label="延期邮件开关">
            <el-switch v-model="notifySettings.deferral_email_enabled" />
            <span style="margin-left:10px;color:#64748b;font-size:13px;">顾客延期成功后发送邮件</span>
          </el-form-item>
          <el-form-item label="管理员组邮箱">
            <el-input
              v-model="notifyEmailsText"
              type="textarea"
              :rows="4"
              placeholder="每行一个邮箱，或用逗号分隔"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="notifySaving" @click="saveNotifySettings">保存</el-button>
            <el-button @click="loadNotifySettings">刷新</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
      <!-- 发送页 -->
      <el-tab-pane label="发送邮件" name="send">
        <div class="send-layout">
          <!-- 左侧：收件人管理 -->
          <div class="left-panel">
            <div class="panel-title">收件人列表 <span class="count-badge">{{ recipients.length }}</span></div>
            
            <el-form :inline="true" class="add-form" @submit.native.prevent>
              <el-input
                v-model="newRecipient.email"
                placeholder="邮箱地址"
                style="width: 220px"
                @keyup.enter.native="addRecipient"
              />
              <el-input
                v-model="newRecipient.username"
                placeholder="姓名（可选）"
                style="width: 140px"
                @keyup.enter.native="addRecipient"
              />
              <el-button type="primary" @click="addRecipient">添加</el-button>
              <el-button @click="showImportDialog = true">批量导入</el-button>
              <el-button @click="loadUsersFromSystem">同步用户</el-button>
            </el-form>

            <div class="recipient-list">
              <div v-if="recipients.length === 0" class="empty-tip">
                暂无收件人，请添加或批量导入
              </div>
              <div v-for="(item, index) in recipients" :key="index" class="recipient-item">
                <div class="recipient-info">
                  <span class="recipient-name">{{ item.username || '未命名' }}</span>
                  <span class="recipient-email">{{ item.email }}</span>
                </div>
                <el-button type="text" size="mini" @click="removeRecipient(index)">删除</el-button>
              </div>
            </div>

            <div class="list-actions" v-if="recipients.length > 0">
              <el-button type="danger" size="small" @click="clearAll">清空全部</el-button>
            </div>
          </div>

          <!-- 右侧：模板和发送设置 -->
          <div class="right-panel">
            <div class="panel-title">邮件内容设置</div>

            <el-form :model="sendForm" label-width="100px" class="send-form">
              <el-form-item label="选择模板">
                <el-select v-model="sendForm.templateKey" @change="onTemplateChange" style="width: 100%">
                  <el-option
                    v-for="t in templateList"
                    :key="t.key"
                    :label="t.name"
                    :value="t.key"
                  />
                </el-select>
              </el-form-item>

              <el-form-item label="邮件主题">
                <el-input
                  v-model="sendForm.customSubject"
                  placeholder="可修改邮件主题"
                />
              </el-form-item>

              <el-form-item label="邮件正文">
                <el-input
                  type="textarea"
                  v-model="sendForm.customContent"
                  :rows="10"
                  placeholder="可自定义修改正文；留空则使用所选模板默认内容"
                />
                <div class="template-hint" v-if="sendForm.templateKey !== 'custom'">
                  当前模板：{{ currentTemplate.name }}（可编辑正文后发送）
                </div>
              </el-form-item>

              <el-form-item label="定时发送">
                <el-switch v-model="sendForm.scheduled" />
              </el-form-item>
              <el-form-item v-if="sendForm.scheduled" label="发送时间">
                <el-date-picker
                  v-model="sendForm.scheduledAt"
                  type="datetime"
                  placeholder="选择发送时间"
                  style="width: 100%"
                  :disabled-date="disabledDate"
                />
              </el-form-item>

              <el-form-item>
                <el-button
                  type="primary"
                  @click="previewEmail"
                  :disabled="recipients.length === 0"
                >预览邮件</el-button>
                <el-button
                  type="success"
                  @click="sendEmails"
                  :disabled="recipients.length === 0"
                  :loading="sending"
                >{{ sendForm.scheduled ? '定时发送' : '立即发送' }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </el-tab-pane>

      <!-- 日志页 -->
      <el-tab-pane label="发送日志" name="logs">
        <div class="logs-header">
          <el-form :inline="true" class="filter-form">
            <el-form-item label="状态">
              <el-select v-model="logFilter.status" placeholder="全部" clearable>
                <el-option label="成功" value="success" />
                <el-option label="失败" value="failed" />
                <el-option label="已定时" value="scheduled" />
              </el-select>
            </el-form-item>
            <el-form-item label="模板">
              <el-select v-model="logFilter.templateKey" placeholder="全部" clearable>
                <el-option
                  v-for="t in templateList"
                  :key="t.key"
                  :label="t.name"
                  :value="t.key"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="开始日期">
              <el-date-picker v-model="logFilter.startDate" type="date" placeholder="开始" />
            </el-form-item>
            <el-form-item label="结束日期">
              <el-date-picker v-model="logFilter.endDate" type="date" placeholder="结束" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="loadLogs">查询</el-button>
              <el-button @click="exportLogs">导出Excel</el-button>
            </el-form-item>
          </el-form>
        </div>

        <el-table :data="logList" v-loading="logsLoading" stripe>
          <el-table-column prop="created_at" label="发送时间" width="180">
            <template slot-scope="scope">
              {{ $formatDateTime(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column prop="to" label="收件邮箱" width="200" />
          <el-table-column prop="recipientName" label="收件人" width="120" />
          <el-table-column prop="templateName" label="模板类型" width="140" />
          <el-table-column prop="subject" label="主题" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag
                :type="scope.row.status === 'success' ? 'success' : (scope.row.status === 'scheduled' ? 'warning' : 'danger')"
                size="small"
              >
                {{ scope.row.status === 'success' ? '成功' : (scope.row.status === 'scheduled' ? '已定时' : '失败') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="备注" show-overflow-tooltip width="200" />
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- 预览对话框 -->
    <el-dialog title="邮件预览" :visible.sync="previewVisible" width="600px">
      <div class="preview-dialog">
        <h3>{{ previewData.subject }}</h3>
        <div v-html="previewData.htmlContent"></div>
      </div>
      <span slot="footer">
        <el-button @click="previewVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 批量导入对话框 -->
    <el-dialog title="批量导入收件人" :visible.sync="showImportDialog" width="500px">
      <div class="import-section">
        <p>每行一个收件人，格式：邮箱,姓名（逗号分隔）</p>
        <el-input
          type="textarea"
          v-model="importText"
          :rows="8"
          placeholder="example@email.com,张三&#10;test@email.com,李四"
        />
      </div>
      <span slot="footer">
        <el-button @click="showImportDialog = false">取消</el-button>
        <el-button type="primary" @click="doImport">导入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { downloadBlobResponse } from '../utils/fileExport'
import { routeTabMixin } from '../utils/routeTab'

const FALLBACK_TEMPLATES = [
  { key: 'custom', name: '自定义邮件', subject: '【温岭EATing】通知' },
  { key: 'register_code', name: '注册验证码', subject: '【温岭EATing】注册验证码' },
  { key: 'reset_password_code', name: '找回密码验证码', subject: '【温岭EATing】找回密码验证码' },
  { key: 'order_success', name: '订餐成功通知', subject: '【温岭EATing】订餐成功通知' },
  { key: 'order_expiring', name: '订餐即将到期提醒', subject: '【温岭EATing】订餐即将到期提醒' },
  { key: 'activity_notice', name: '活动通知', subject: '【温岭EATing】活动通知' }
]

export default {
  name: 'EmailBatch',
  mixins: [
    routeTabMixin({
      allowed: ['notify', 'send', 'logs']
    })
  ],
  data() {
    return {
      activeTab: 'send',
      notifySettings: { emails: [], deferral_email_enabled: true },
      notifyEmailsText: '',
      notifySaving: false,
      templateList: [],
      recipients: [],
      newRecipient: { email: '', username: '' },
      importText: '',
      showImportDialog: false,
      sending: false,
      sendForm: {
        templateKey: 'order_success',
        customSubject: '',
        customContent: '',
        scheduled: false,
        scheduledAt: null
      },
      currentTemplate: { name: '', subject: '', preview: '' },
      logFilter: { status: '', templateKey: '', startDate: '', endDate: '' },
      logList: [],
      logsLoading: false,
      previewVisible: false,
      previewData: { subject: '', htmlContent: '' }
    }
  },
  created() {
    this.loadTemplates();
    this.loadLogs();
    this.loadNotifySettings();
  },
  methods: {
    async loadNotifySettings() {
      try {
        const resp = await this.$api.get('/admin/notify-settings')
        if (resp && resp.code === 200 && resp.data) {
          this.notifySettings = {
            emails: Array.isArray(resp.data.emails) ? resp.data.emails : [],
            deferral_email_enabled: resp.data.deferral_email_enabled !== false
          }
          this.notifyEmailsText = this.notifySettings.emails.join('\n')
        }
      } catch (e) {
        this.$message.error('加载管理员通知邮箱失败')
      }
    },
    async saveNotifySettings() {
      const emails = String(this.notifyEmailsText || '')
        .split(/[\n,;，；\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      this.notifySaving = true
      try {
        const resp = await this.$api.put('/admin/notify-settings', {
          emails,
          deferral_email_enabled: this.notifySettings.deferral_email_enabled
        })
        if (resp && resp.code === 200) {
          this.$message.success('已保存')
          await this.loadNotifySettings()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || '保存失败')
      } finally {
        this.notifySaving = false
      }
    },
    applyFallbackTemplates() {
      if (this.templateList.length > 0) return
      this.templateList = FALLBACK_TEMPLATES.slice()
      if (!this.templateList.find(t => t.key === this.sendForm.templateKey)) {
        this.sendForm.templateKey = this.templateList[0].key
      }
      this.onTemplateChange()
    },
    async loadTemplates() {
      try {
        const resp = await this.$api.get('/admin/email/templates')
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.templateList = resp.data
          if (this.templateList.length > 0) {
            if (!this.templateList.find(t => t.key === this.sendForm.templateKey)) {
              this.sendForm.templateKey = this.templateList[0].key
            }
            this.onTemplateChange()
          } else {
            this.applyFallbackTemplates()
          }
        } else {
          this.applyFallbackTemplates()
          this.$message.error((resp && resp.message) || '获取模板列表失败')
        }
      } catch (e) {
        this.applyFallbackTemplates()
        this.$message.error(formatApiError(e, '获取模板列表失败'))
      }
    },
    onTemplateChange() {
      const template = this.templateList.find(t => t.key === this.sendForm.templateKey);
      if (template) {
        this.currentTemplate = {
          name: template.name,
          subject: template.subject,
          preview: this.getTemplatePreview(template.key)
        };
        this.sendForm.customSubject = template.subject || '';
        this.sendForm.customContent = this.getTemplatePreview(template.key);
      }
    },
    getTemplatePreview(key) {
      const previews = {
        register_code: '【注册验证码】\n您的验证码为：XXXXXX\n有效期5分钟',
        reset_password_code: '【找回密码验证码】\n您的验证码为：XXXXXX\n有效期5分钟',
        order_success: '【订餐成功通知】\n含收件人、联系方式、收货地址、订单备注、套餐名称/周期/金额等订单详情（支付成功后自动发送）',
        order_expiring: '【订餐即将到期提醒】\n套餐剩余餐次≤10 餐时自动发送，含订单详情与续订提示',
        activity_notice: '【活动通知】\n尊敬的用户，平台有新活动，敬请关注！'
      };
      return previews[key] || '自定义邮件模板';
    },
    addRecipient() {
      const { email, username } = this.newRecipient;
      if (!email || !this.isValidEmail(email)) {
        this.$message.warning('请输入有效的邮箱地址');
        return;
      }
      if (this.recipients.find(r => r.email === email)) {
        this.$message.warning('该邮箱已在列表中');
        return;
      }
      this.recipients.push({ email, username: username || '' });
      this.newRecipient = { email: '', username: '' };
    },
    removeRecipient(index) {
      this.recipients.splice(index, 1);
    },
    clearAll() {
      this.$confirm('确定清空全部收件人？', '提示', { type: 'warning' })
        .then(() => { this.recipients = []; })
        .catch(() => {});
    },
    async loadUsersFromSystem() {
      try {
        const resp = await this.$api.get('/admin/email/user-list')
        if (resp && resp.code === 200) {
          const newUsers = resp.data.filter(u =>
            !this.recipients.find(r => r.email === u.email)
          );
          this.recipients = [...this.recipients, ...newUsers];
          this.$message.success(`已同步 ${newUsers.length} 个用户`);
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '同步用户失败'))
      }
    },
    doImport() {
      const lines = this.importText.trim().split('\n');
      let imported = 0;
      lines.forEach(line => {
        const parts = line.split(',');
        const email = parts[0].trim();
        const username = (parts[1] || '').trim();
        if (email && this.isValidEmail(email) && !this.recipients.find(r => r.email === email)) {
          this.recipients.push({ email, username });
          imported++;
        }
      });
      this.showImportDialog = false;
      this.importText = '';
      this.$message.success(`成功导入 ${imported} 个收件人`);
    },
    isValidEmail(email) {
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    },
    async previewEmail() {
      if (this.recipients.length === 0) {
        this.$message.warning('请先添加收件人');
        return;
      }
      const subject = this.sendForm.customSubject || this.currentTemplate.subject || '通知邮件';
      const content = this.sendForm.customContent || this.currentTemplate.preview || '';
      this.previewData = {
        subject,
        htmlContent: `<div style="font-family:Arial,sans-serif;padding:20px;">
          <h2 style="color:#333;margin-bottom:20px;">${subject}</h2>
          <div style="line-height:1.8;color:#666;white-space:pre-wrap;">${content}</div>
          <div style="margin-top:30px;padding-top:20px;border-top:1px solid #e8e8e8;text-align:center;color:#999;font-size:12px;">
            <div>本邮件自动发送，请勿回复</div>
            <div>温岭EATing创意融合轻食</div>
          </div>
        </div>`
      };
      this.previewVisible = true;
    },
    async sendEmails() {
      if (this.recipients.length === 0) {
        this.$message.warning('请先添加收件人');
        return;
      }
      if (this.sendForm.scheduled && !this.sendForm.scheduledAt) {
        this.$message.warning('请选择定时发送时间');
        return;
      }
      const confirmText = this.sendForm.scheduled
        ? `确定于 ${this.$formatDateTime(this.sendForm.scheduledAt)} 向 ${this.recipients.length} 位收件人发送？`
        : `确定向 ${this.recipients.length} 位收件人立即发送邮件？`;
      this.$confirm(confirmText, '确认发送', { type: 'warning' })
        .then(async () => {
          this.sending = true;
          try {
            const payload = {
              recipients: this.recipients,
              templateKey: this.sendForm.templateKey,
              customSubject: this.sendForm.customSubject,
              customContent: this.sendForm.customContent
            };
            if (this.sendForm.scheduled && this.sendForm.scheduledAt) {
              payload.scheduledAt = this.sendForm.scheduledAt;
            }
            const resp = await this.$api.post('/admin/email/batch-send', payload)
            if (resp && resp.code === 200) {
              this.$message.success(resp.message || '发送完成');
              this.loadLogs();
              this.activeTab = 'logs';
            } else {
              this.$message.error((resp && resp.message) || '发送失败');
            }
          } catch (e) {
            this.$message.error('发送失败：' + formatApiError(e, '未知错误'))
          } finally {
            this.sending = false;
          }
        })
        .catch(() => {});
    },
    async loadLogs() {
      this.logsLoading = true
      try {
        const params = {}
        if (this.logFilter.status) params.status = this.logFilter.status
        if (this.logFilter.templateKey) params.templateKey = this.logFilter.templateKey
        if (this.logFilter.startDate) params.startDate = this.logFilter.startDate
        if (this.logFilter.endDate) params.endDate = this.logFilter.endDate
        const resp = await this.$api.get('/admin/email/logs', { params })
        if (resp && resp.code === 200) {
          this.logList = Array.isArray(resp.data) ? resp.data : []
        } else {
          this.logList = []
          this.$message.error((resp && resp.message) || '获取日志失败')
        }
      } catch (e) {
        this.logList = []
        this.$message.error(formatApiError(e, '获取日志失败'))
      } finally {
        this.logsLoading = false
      }
    },
    async exportLogs() {
      try {
        const params = {};
        if (this.logFilter.status) params.status = this.logFilter.status;
        if (this.logFilter.templateKey) params.templateKey = this.logFilter.templateKey;
        if (this.logFilter.startDate) params.startDate = this.logFilter.startDate;
        if (this.logFilter.endDate) params.endDate = this.logFilter.endDate;
        const resp = await this.$api.get('/admin/email/logs/export', { params, responseType: 'blob' })
        downloadBlobResponse({ data: resp, headers: {} }, `邮件发送日志_${new Date().toISOString().slice(0, 10)}`)
      } catch (e) {
        this.$message.error(formatApiError(e, '导出失败'))
      }
    },
    disabledDate(time) {
      return time.getTime() < Date.now();
    }
  }
}
</script>

<style scoped>
.email-batch-container {
  padding: 20px;
}
.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
  color: #303133;
}
.page-desc {
  margin: 0 0 20px 0;
  color: #909399;
  font-size: 14px;
}
.send-layout {
  display: flex;
  gap: 20px;
}
.left-panel, .right-panel {
  flex: 1;
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  min-height: 500px;
}
.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}
.count-badge {
  background: #667eea;
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
}
.add-form {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.recipient-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
}
.empty-tip {
  padding: 40px;
  text-align: center;
  color: #c0c4cc;
  font-size: 14px;
}
.recipient-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f2f6fc;
}
.recipient-item:last-child {
  border-bottom: none;
}
.recipient-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.recipient-name {
  font-size: 14px;
  color: #303133;
}
.recipient-email {
  font-size: 12px;
  color: #909399;
}
.list-actions {
  margin-top: 12px;
  text-align: right;
}
.send-form {
  margin-top: 16px;
}
.template-preview {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}
.preview-header {
  background: #f5f7fa;
  padding: 10px 14px;
  font-weight: 500;
  font-size: 14px;
}
.preview-content {
  padding: 14px;
  max-height: 200px;
  overflow-y: auto;
}
.preview-content pre {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
  white-space: pre-wrap;
  font-family: inherit;
}
.logs-header {
  margin-bottom: 16px;
}
.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
}
.preview-dialog h3 {
  margin: 0 0 16px 0;
  color: #303133;
}
.preview-dialog {
  line-height: 1.8;
}
.import-section {
  margin-bottom: 16px;
}
.import-section p {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 14px;
}
.template-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
</style>
